import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";

const emptyFormulaForm = () => ({
  name: "",
  code: "",
  description: "",
  expression_text: "",
  output_type: "currency",
  scope_type: "budget_line",
  status: "draft",
});

export default function FormulaManager() {
  const { companyId } = useCompany();

  const [sessionUserId, setSessionUserId] = useState("");
  const [formulas, setFormulas] = useState([]);
  const [selectedFormulaId, setSelectedFormulaId] = useState("");
  const [dependencies, setDependencies] = useState([]);
  const [validationRuns, setValidationRuns] = useState([]);
  const [form, setForm] = useState(emptyFormulaForm());

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const selectedFormula = useMemo(
    () => formulas.find((f) => f.id === selectedFormulaId) || null,
    [formulas, selectedFormulaId]
  );

  const resetMessages = () => {
    setErr("");
    setSuccess("");
  };

  const loadSession = useCallback(async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();

    if (error) throw error;
    setSessionUserId(user?.id || "");
  }, []);

  const loadFormulas = useCallback(async () => {
    if (!companyId) return;

    const { data, error } = await supabase
      .from("planning_formulas")
      .select("*")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (error) throw error;

    setFormulas(data || []);
    if (!selectedFormulaId && data?.length) {
      setSelectedFormulaId(data[0].id);
    }
  }, [companyId, selectedFormulaId]);

  const loadDependencies = useCallback(async () => {
    if (!selectedFormulaId) {
      setDependencies([]);
      setValidationRuns([]);
      return;
    }

    const [{ data: deps, error: depErr }, { data: runs, error: runErr }] =
      await Promise.all([
        supabase
          .from("planning_formula_dependencies")
          .select("*")
          .eq("formula_id", selectedFormulaId),
        supabase
          .from("planning_formula_validation_runs")
          .select("*")
          .eq("formula_id", selectedFormulaId)
          .order("created_at", { ascending: false }),
      ]);

    if (depErr) throw depErr;
    if (runErr) throw runErr;

    setDependencies(deps || []);
    setValidationRuns(runs || []);
  }, [selectedFormulaId]);

  const initialize = useCallback(async () => {
    try {
      setLoading(true);
      await loadSession();
      await loadFormulas();
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to load formulas.");
    } finally {
      setLoading(false);
    }
  }, [loadFormulas, loadSession]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    loadDependencies().catch((e) => {
      console.error(e);
      setErr(e.message || "Failed to load formula details.");
    });
  }, [loadDependencies]);

  const createFormula = async (e) => {
    e.preventDefault();
    resetMessages();

    try {
      setSaving(true);

      const { data, error } = await supabase
        .from("planning_formulas")
        .insert({
          company_id: companyId,
          name: form.name.trim(),
          code: form.code.trim(),
          description: form.description.trim() || null,
          expression_text: form.expression_text.trim(),
          output_type: form.output_type,
          scope_type: form.scope_type,
          status: form.status,
          created_by: sessionUserId,
          updated_by: sessionUserId,
        })
        .select()
        .single();

      if (error) throw error;

      setForm(emptyFormulaForm());
      setSuccess("Formula created successfully.");
      await loadFormulas();
      setSelectedFormulaId(data.id);
    } catch (e2) {
      console.error(e2);
      setErr(e2.message || "Failed to create formula.");
    } finally {
      setSaving(false);
    }
  };

  const validateFormula = async () => {
    if (!selectedFormulaId) return;
    resetMessages();

    try {
      setSaving(true);

      const { data, error } = await supabase.rpc("validate_planning_formula", {
        p_formula_id: selectedFormulaId,
      });

      if (error) throw error;

      const first = Array.isArray(data) ? data[0] : null;
      if (first?.is_valid) {
        setSuccess("Formula validated successfully.");
      } else {
        setErr(first?.error_message || "Formula validation failed.");
      }

      await loadDependencies();
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to validate formula.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="kp-card">Loading formulas…</div>;
  }

  return (
    <div className="kp-grid-2">
      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Formula Manager</h3>
        </div>

        <form className="kp-form" onSubmit={createFormula}>
          <div className="kp-form-grid">
            <div className="kp-field">
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>Code</label>
              <input
                value={form.code}
                onChange={(e) =>
                  setForm((p) => ({ ...p, code: e.target.value }))
                }
                required
              />
            </div>

            <div className="kp-field">
              <label>Output Type</label>
              <select
                value={form.output_type}
                onChange={(e) =>
                  setForm((p) => ({ ...p, output_type: e.target.value }))
                }
              >
                <option value="currency">currency</option>
                <option value="number">number</option>
                <option value="percentage">percentage</option>
              </select>
            </div>

            <div className="kp-field">
              <label>Scope Type</label>
              <select
                value={form.scope_type}
                onChange={(e) =>
                  setForm((p) => ({ ...p, scope_type: e.target.value }))
                }
              >
                <option value="budget_line">budget_line</option>
                <option value="forecast_line">forecast_line</option>
                <option value="scenario">scenario</option>
                <option value="global">global</option>
              </select>
            </div>
          </div>

          <div className="kp-field">
            <label>Description</label>
            <textarea
              rows="2"
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
            />
          </div>

          <div className="kp-field">
            <label>Expression</label>
            <textarea
              rows="5"
              value={form.expression_text}
              onChange={(e) =>
                setForm((p) => ({ ...p, expression_text: e.target.value }))
              }
              placeholder={`Examples:
DRIVER(employee_count)
DRIVER(avg_salary) * 12
ACCOUNT(601001) * 0.05
FORMULA(gross_profit) * 0.02`}
              required
            />
          </div>

          <button className="kp-btn kp-btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Create Formula"}
          </button>
        </form>

        {err && <div className="kp-alert kp-alert-error">{err}</div>}
        {success && <div className="kp-alert kp-alert-success">{success}</div>}

        <div className="kp-list-simple">
          {formulas.map((formula) => (
            <button
              key={formula.id}
              className={`kp-budget-item ${
                selectedFormulaId === formula.id ? "selected" : ""
              }`}
              onClick={() => setSelectedFormulaId(formula.id)}
            >
              <div className="kp-budget-item-top">
                <strong>{formula.name}</strong>
                <span className="kp-badge status-draft">{formula.code}</span>
              </div>
              <div className="kp-budget-item-sub">{formula.scope_type}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="kp-card">
        <div className="kp-card-head">
          <h3>Formula Validation</h3>
          {!!selectedFormula && (
            <button className="kp-btn kp-btn-soft" onClick={validateFormula}>
              Validate
            </button>
          )}
        </div>

        {!selectedFormula ? (
          <div className="kp-note">Select a formula first.</div>
        ) : (
          <>
            <div className="kp-note">
              <strong>{selectedFormula.name}</strong>
              <br />
              {selectedFormula.expression_text}
            </div>

            <div className="kp-card-head">
              <h4>Dependencies</h4>
            </div>

            <div className="kp-table-wrap">
              <table className="kp-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Reference ID</th>
                  </tr>
                </thead>
                <tbody>
                  {dependencies.length === 0 ? (
                    <tr>
                      <td colSpan="2" className="kp-empty-cell">
                        No dependencies yet.
                      </td>
                    </tr>
                  ) : (
                    dependencies.map((dep) => (
                      <tr key={dep.id}>
                        <td>{dep.dependency_type}</td>
                        <td>{dep.dependency_ref_id}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="kp-card-head" style={{ marginTop: 16 }}>
              <h4>Validation Runs</h4>
            </div>

            <div className="kp-table-wrap">
              <table className="kp-table">
                <thead>
                  <tr>
                    <th>Valid</th>
                    <th>Dependencies</th>
                    <th>Error</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {validationRuns.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="kp-empty-cell">
                        No validation runs yet.
                      </td>
                    </tr>
                  ) : (
                    validationRuns.map((run) => (
                      <tr key={run.id}>
                        <td>{run.is_valid ? "Yes" : "No"}</td>
                        <td>{run.dependency_count}</td>
                        <td>{run.error_message || "—"}</td>
                        <td>{new Date(run.created_at).toLocaleString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
